import { createContext, useContext, useState, useEffect } from "react";
import { format } from "date-fns";
import { hu } from "date-fns/locale";
import { Phone, ArrowRight, Check, Clock, User, Video } from "lucide-react";
import { toast } from "sonner";

import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "./ui/dialog";
import { Input } from "./ui/input";
import { Textarea } from "./ui/textarea";
import { Label } from "./ui/label";
import { Button } from "./ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "./ui/select";
import { Calendar } from "./ui/calendar";

import { SERVICES, OFFICES, TIME_SLOTS, MODES } from "../data/constants";
import { createBooking, createCallback, getAvailability } from "../lib/api";

const DialogCtx = createContext(null);
export const useContactDialogs = () => useContext(DialogCtx);

const label = "overline block mb-2 text-muted-foreground";
const inputCls =
  "rounded-none border border-border bg-white h-11 focus-visible:ring-0 focus-visible:border-foreground transition-[border-color]";
const selectCls =
  "rounded-none border border-border bg-white px-3 h-11 focus:ring-0 data-[state=open]:border-foreground";

function BookingDialog({ open, onOpenChange }) {
  const empty = { name: "", phone: "", email: "", service: "", office: "", mode: "Személyes", time: "", message: "" };
  const [form, setForm] = useState(empty);
  const [date, setDate] = useState(null);
  const [loading, setLoading] = useState(false);
  const [taken, setTaken] = useState([]);
  const [checking, setChecking] = useState(false);

  const set = (k) => (v) => setForm((f) => ({ ...f, [k]: v }));

  // Fetch availability when date changes (single advisor -> global per date)
  useEffect(() => {
    const run = async () => {
      if (!date) {
        setTaken([]);
        return;
      }
      setChecking(true);
      try {
        const res = await getAvailability(format(date, "yyyy-MM-dd"));
        setTaken(res.taken || []);
        if (form.time && (res.taken || []).includes(form.time)) {
          setForm((f) => ({ ...f, time: "" }));
        }
      } catch {
        setTaken([]);
      } finally {
        setChecking(false);
      }
    };
    run();
  }, [date]);

  const reset = () => { setForm(empty); setDate(null); setTaken([]); };

  const submit = async (e) => {
    e.preventDefault();
    const needsOffice = form.mode === "Személyes";
    if (!form.name || !form.phone || !form.service || !date || !form.time || (needsOffice && !form.office)) {
      toast.error("Kérjük, töltse ki a kötelező mezőket.");
      return;
    }
    setLoading(true);
    try {
      await createBooking({ ...form, office: needsOffice ? form.office : "", date: format(date, "yyyy-MM-dd") });
      toast.success("Foglalási kérését rögzítettük!", {
        description: "Miután megerősítettük, emailben visszaigazoljuk az időpontot.",
      });
      reset();
      onOpenChange(false);
    } catch (err) {
      if (err?.response?.status === 409) {
        toast.error("Ezt az időpontot időközben lefoglalták.", {
          description: "Kérjük, válasszon másik szabad időpontot.",
        });
        try {
          const res = await getAvailability(format(date, "yyyy-MM-dd"));
          setTaken(res.taken || []);
          setForm((f) => ({ ...f, time: "" }));
        } catch { /* ignore */ }
      } else {
        toast.error("Hiba történt. Kérjük, próbálja újra.");
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { onOpenChange(o); if (!o) reset(); }}>
      <DialogContent
        data-testid="booking-dialog"
        data-lenis-prevent
        className="rounded-none border border-foreground bg-background p-0 sm:max-w-3xl max-h-[94vh] overflow-y-auto gap-0"
      >
        <DialogHeader className="text-left space-y-2 p-8 pb-6 border-b border-border">
          <span className="overline text-muted-foreground">Foglalás</span>
          <DialogTitle className="font-serif-display text-3xl font-medium tracking-tight">
            Időpontfoglalás
          </DialogTitle>
          <DialogDescription className="text-muted-foreground">
            Válassza ki a konzultáció módját, majd a naptárból egy szabad időpontot.
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={submit} className="p-8 space-y-8" data-testid="booking-form">
          {/* Step 1 — Iroda & szolgáltatás */}
          <div>
            <div className="flex items-center gap-3 mb-5">
              <span className="font-mono-label text-xs">01</span>
              <span className="text-sm font-medium">Konzultáció módja, iroda és szolgáltatás</span>
            </div>
            <div className="mb-5">
              <Label className={label}>Konzultáció módja *</Label>
              <div className="grid grid-cols-3 gap-2">
                {MODES.map((m) => {
                  const active = form.mode === m.value;
                  const Icon = m.id === "szemelyes" ? User : m.id === "telefonos" ? Phone : Video;
                  return (
                    <button
                      key={m.id}
                      type="button"
                      data-testid={`booking-mode-${m.id}`}
                      aria-label={m.label}
                      onClick={() => set("mode")(m.value)}
                      className={`h-11 border text-sm flex items-center justify-center gap-2 transition-[background-color,border-color,color]
                        ${active ? "border-foreground bg-foreground text-background" : "border-border bg-white hover:border-foreground"}`}
                    >
                      <Icon className="h-4 w-4" />
                      <span className="hidden sm:inline">{m.label}</span>
                    </button>
                  );
                })}
              </div>
            </div>
            <div className={form.mode === "Személyes" ? "grid grid-cols-1 sm:grid-cols-2 gap-5" : "grid grid-cols-1 gap-5"}>
              {form.mode === "Személyes" && (
                <div>
                  <Label className={label}>Iroda *</Label>
                  <Select value={form.office} onValueChange={set("office")}>
                    <SelectTrigger data-testid="booking-office" className={selectCls}>
                      <SelectValue placeholder="Válasszon irodát" />
                    </SelectTrigger>
                    <SelectContent className="rounded-none">
                      {OFFICES.map((o) => (
                        <SelectItem key={o.id} value={o.address} className="rounded-none">{o.city} — {o.address}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}
              <div>
                <Label className={label}>Szolgáltatás *</Label>
                <Select value={form.service} onValueChange={set("service")}>
                  <SelectTrigger data-testid="booking-service" className={selectCls}>
                    <SelectValue placeholder="Válasszon" />
                  </SelectTrigger>
                  <SelectContent className="rounded-none">
                    {SERVICES.map((s) => (
                      <SelectItem key={s.id} value={s.name} className="rounded-none">{s.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            {form.mode !== "Személyes" && (
              <p className="text-xs text-muted-foreground mt-3">
                {form.mode === "Telefonos" ? "Telefonos" : "Videóhívásos"} konzultációhoz nem szükséges irodát választani — a megadott elérhetőségen keresünk.
              </p>
            )}
          </div>

          {/* Step 2 — Naptár & időpont */}
          <div className="border-t border-border pt-8">
            <div className="flex items-center gap-3 mb-5">
              <span className="font-mono-label text-xs">02</span>
              <span className="text-sm font-medium">Dátum és időpont</span>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              <div className="border border-border bg-white p-2 flex justify-center">
                <Calendar
                  mode="single"
                  selected={date}
                  onSelect={setDate}
                  locale={hu}
                  disabled={(d) => {
                    const startOfToday = new Date(new Date().setHours(0, 0, 0, 0));
                    if (d < startOfToday || d.getDay() === 0 || d.getDay() === 6) return true;
                    // Személyes konzultáció nem foglalható aznapra
                    if (form.mode === "Személyes" && d.getTime() === startOfToday.getTime()) return true;
                    return false;
                  }}
                  initialFocus
                />
              </div>

              <div data-testid="time-slots">
                <div className="flex items-center justify-between mb-3">
                  <Label className="overline text-muted-foreground mb-0">
                    {date ? format(date, "yyyy. MMMM d.", { locale: hu }) : "Válasszon napot"}
                  </Label>
                  {checking && <span className="text-xs text-muted-foreground animate-pulse">Ellenőrzés…</span>}
                </div>

                {!date ? (
                  <div className="border border-dashed border-border h-full min-h-[220px] flex items-center justify-center text-center p-6">
                    <p className="text-sm text-muted-foreground">
                      Válasszon egy napot a szabad időpontok megjelenítéséhez.
                    </p>
                  </div>
                ) : (
                  <>
                    <div className="grid grid-cols-2 gap-3">
                      {TIME_SLOTS.map((t) => {
                        const isTaken = taken.includes(t);
                        const active = form.time === t;
                        return (
                          <button
                            key={t}
                            type="button"
                            disabled={isTaken}
                            data-testid={`slot-${t}`}
                            onClick={() => set("time")(t)}
                            className={`h-12 border text-sm flex items-center justify-center gap-2 transition-[background-color,border-color,color]
                              ${isTaken
                                ? "border-border bg-secondary text-muted-foreground/50 line-through cursor-not-allowed"
                                : active
                                  ? "border-foreground bg-foreground text-background"
                                  : "border-border bg-white hover:border-foreground"}`}
                          >
                            <Clock className="h-3.5 w-3.5" />
                            {t}
                            {active && <Check className="h-3.5 w-3.5" />}
                          </button>
                        );
                      })}
                    </div>
                    <div className="flex items-center gap-5 mt-4 text-xs text-muted-foreground">
                      <span className="flex items-center gap-2"><span className="h-3 w-3 border border-border bg-white inline-block" /> Szabad</span>
                      <span className="flex items-center gap-2"><span className="h-3 w-3 bg-foreground inline-block" /> Kiválasztva</span>
                      <span className="flex items-center gap-2"><span className="h-3 w-3 bg-secondary border border-border inline-block" /> Foglalt</span>
                    </div>
                  </>
                )}
              </div>
            </div>
          </div>

          {/* Step 3 — Elérhetőség */}
          <div className="border-t border-border pt-8">
            <div className="flex items-center gap-3 mb-5">
              <span className="font-mono-label text-xs">03</span>
              <span className="text-sm font-medium">Az Ön adatai</span>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
              <div>
                <Label className={label}>Név *</Label>
                <Input data-testid="booking-name" className={inputCls} value={form.name}
                  onChange={(e) => set("name")(e.target.value)} placeholder="Teljes név" />
              </div>
              <div>
                <Label className={label}>Telefonszám *</Label>
                <Input data-testid="booking-phone" className={inputCls} value={form.phone}
                  onChange={(e) => set("phone")(e.target.value)} placeholder="+36 ..." />
              </div>
            </div>
            <div className="mt-5">
              <Label className={label}>E-mail</Label>
              <Input data-testid="booking-email" className={inputCls} value={form.email}
                onChange={(e) => set("email")(e.target.value)} placeholder="pelda@email.hu" />
            </div>
            <div className="mt-5">
              <Label className={label}>Megjegyzés</Label>
              <Textarea data-testid="booking-message" value={form.message}
                onChange={(e) => set("message")(e.target.value)}
                className="rounded-none border border-border bg-white focus-visible:ring-0 focus-visible:border-foreground min-h-[80px]"
                placeholder="Miben segíthetünk?" />
            </div>
          </div>

          <Button type="submit" disabled={loading} data-testid="booking-submit"
            className="w-full rounded-none bg-foreground text-background hover:bg-[#2A2A2A] h-13 py-4 group">
            {loading ? "Küldés..." : "Foglalás megerősítése"}
            <ArrowRight className="ml-2 h-4 w-4 transition-transform group-hover:translate-x-1" />
          </Button>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function CallbackDialog({ open, onOpenChange }) {
  const empty = { name: "", phone: "", email: "", preferred_time: "", service: "", message: "" };
  const [form, setForm] = useState(empty);
  const [loading, setLoading] = useState(false);
  const set = (k) => (v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.phone || !form.email) {
      toast.error("Kérjük, adja meg a nevét, telefonszámát és email címét.");
      return;
    }
    setLoading(true);
    try {
      await createCallback(form);
      toast.success("Visszahívási kérés elküldve!", {
        description: "Munkatársunk a megadott időpontban visszahívja Önt.",
      });
      setForm(empty);
      onOpenChange(false);
    } catch (err) {
      toast.error("Hiba történt. Kérjük, próbálja újra.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        data-testid="callback-dialog"
        data-lenis-prevent
        className="rounded-none border border-foreground bg-background p-0 sm:max-w-lg gap-0"
      >
        <DialogHeader className="text-left space-y-2 p-8 pb-6 border-b border-border">
          <span className="overline text-muted-foreground">Visszahívás</span>
          <DialogTitle className="font-serif-display text-3xl font-medium tracking-tight">
            Telefonos visszahívás
          </DialogTitle>
          <DialogDescription className="text-muted-foreground">
            Hagyja meg elérhetőségét, és díjmentesen visszahívjuk Önt.
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={submit} className="p-8 space-y-5" data-testid="callback-form">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
            <div>
              <Label className={label}>Név *</Label>
              <Input data-testid="callback-name" className={inputCls} value={form.name}
                onChange={(e) => set("name")(e.target.value)} placeholder="Teljes név" />
            </div>
            <div>
              <Label className={label}>Telefonszám *</Label>
              <Input data-testid="callback-phone" className={inputCls} value={form.phone}
                onChange={(e) => set("phone")(e.target.value)} placeholder="+36 ..." />
            </div>
          </div>

          <div>
            <Label className={label}>Email cím *</Label>
            <Input data-testid="callback-email" type="email" className={inputCls} value={form.email}
              onChange={(e) => set("email")(e.target.value)} placeholder="pelda@email.hu" />
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
            <div>
              <Label className={label}>Mikor hívjuk?</Label>
              <Input data-testid="callback-time" className={inputCls} value={form.preferred_time}
                onChange={(e) => set("preferred_time")(e.target.value)} placeholder="pl. délután 15 óra" />
            </div>
            <div>
              <Label className={label}>Téma</Label>
              <Select value={form.service} onValueChange={set("service")}>
                <SelectTrigger data-testid="callback-service" className={selectCls}>
                  <SelectValue placeholder="Válasszon" />
                </SelectTrigger>
                <SelectContent className="rounded-none">
                  {SERVICES.map((s) => (
                    <SelectItem key={s.id} value={s.name} className="rounded-none">{s.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          <div>
            <Label className={label}>Megjegyzés</Label>
            <Textarea data-testid="callback-message" value={form.message}
              onChange={(e) => set("message")(e.target.value)}
              className="rounded-none border border-border bg-white focus-visible:ring-0 focus-visible:border-foreground min-h-[80px]"
              placeholder="Miben segíthetünk?" />
          </div>

          <Button type="submit" disabled={loading} data-testid="callback-submit"
            className="w-full rounded-none bg-foreground text-background hover:bg-[#2A2A2A] h-12 group">
            <Phone className="mr-2 h-4 w-4" />
            {loading ? "Küldés..." : "Visszahívást kérek"}
          </Button>
        </form>
      </DialogContent>
    </Dialog>
  );
}

export function ContactDialogProvider({ children }) {
  const [booking, setBooking] = useState(false);
  const [callback, setCallback] = useState(false);

  return (
    <DialogCtx.Provider
      value={{
        openBooking: () => setBooking(true),
        openCallback: () => setCallback(true),
      }}
    >
      {children}
      <BookingDialog open={booking} onOpenChange={setBooking} />
      <CallbackDialog open={callback} onOpenChange={setCallback} />
    </DialogCtx.Provider>
  );
}
